import type { ExpenseTransaction, Receipt, ReconciliationSummary, ResponsibleUser } from './receiptApi';

export type SimpleRecord = {
    id: number;
    name?: string | null;
    email?: string;
    project_no?: number;
    address?: string | null;
    qbo_class_id?: string | null;
    qbo_class_ids?: string[];
    qbo_classes?: { qbo_id: string; name: string }[];
    legacy_mrt_construction_qb?: boolean;
};

/** Note for employees when the job has no Class in MRT Roofing QuickBooks yet. */
export const projectClassEmployeeNote = (
    project: SimpleRecord | null | undefined
): string | null => {
    if (
        !project ||
        project.qbo_class_id ||
        (project.qbo_class_ids && project.qbo_class_ids.length > 0)
    ) {
        return null;
    }
    if (project.legacy_mrt_construction_qb) {
        return 'Legacy MRT Construction QB — post it to the admin; they will open a Class in MRT Roofing when they verify.';
    }
    return "This job isn't linked in QuickBooks yet. Post it to the admin — they'll create the Class when they verify.";
};

export const EMPTY_SUMMARY: ReconciliationSummary = {
    posted: 0,
    saved_receipts: 0,
    matched: 0,
    discrepancies: 0,
    missing_receipts: 0,
    unmatched_uploads: 0,
    approved: 0,
    needs_account: 0,
};

export type SummaryFilter = keyof ReconciliationSummary;

export type QuickRange = 'current_week' | 'previous_week' | 'current_month' | 'previous_month';

export const TRACKER_FILTERS: SummaryFilter[] = [
    'posted',
    'discrepancies',
    'missing_receipts',
    'matched',
    'approved',
    'unmatched_uploads',
    'needs_account',
];

export const SUMMARY_LIST_FILTER: Partial<
    Record<SummaryFilter, Record<string, unknown>>
> = {
    matched: { status: 'matched' },
    discrepancies: { status: 'discrepancy' },
    missing_receipts: { status: 'missing_receipt' },
    approved: { approved: true },
    needs_account: { needs_account: true },
    posted: { posted: true },
};

export const SUMMARY_TRANSACTION_STATUS: Partial<Record<SummaryFilter, string>> = {
    matched: 'matched',
    discrepancies: 'discrepancy',
    missing_receipts: 'missing_receipt',
};

// ponytail: merged logins matched by display name; move to a DB column if more merges are needed.
const MERGED_LOGINS = [['Admin', 'Alejandro Martinez']];

/** primaryId: the login new assignments go to (the member with a card). */
export type EmployeeGroup = { key: string; label: string; ids: number[]; primaryId: number };

/** Cardholders only (people without a card never have charges), with merged logins shown as one entry. */
export const employeeGroups = (users: ResponsibleUser[]): EmployeeGroup[] => {
    const name = (user: ResponsibleUser) => user.name || user.email;
    const groups: EmployeeGroup[] = [];
    const used = new Set<number>();
    for (const names of MERGED_LOGINS) {
        const members = users.filter(user => names.includes(name(user)));
        if (!members.some(user => user.has_card)) continue;
        members.forEach(user => used.add(user.id));
        const ids = members.map(user => user.id);
        const primaryId = (members.find(user => user.has_card) ?? members[0]).id;
        groups.push({ key: ids.join(','), label: names.join(' / '), ids, primaryId });
    }
    for (const user of users) {
        if (!user.has_card || used.has(user.id)) continue;
        groups.push({ key: String(user.id), label: name(user), ids: [user.id], primaryId: user.id });
    }
    return groups.sort((a, b) => a.label.localeCompare(b.label));
};

export const statusColor = (status: string) => {
    if (status === 'matched') return 'success';
    if (status === 'discrepancy') return 'warning';
    return 'error';
};

export const allocationColor = (status: Receipt['allocation_status']) => {
    if (status === 'verified') return 'success';
    if (status === 'needs_correction') return 'error';
    return 'warning';
};

const pad = (value: number) => String(value).padStart(2, '0');

export const toLocalDateKey = (d: Date): string => {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const localToday = () => {
    return toLocalDateKey(new Date());
};

export const projectOptionLabel = (project: SimpleRecord) => {
    const no = project.project_no != null ? `${project.project_no} — ` : '';
    const title = project.name || project.address || '';
    return `${no}${title}`;
};

/**
 * Compute transaction_date_gte and _lte for quick filter ranges.
 * Weeks start Monday; months use local time boundaries.
 */
export const quickDateRange = (
    range: QuickRange,
    today = new Date()
): { transaction_date_gte: string; transaction_date_lte: string } => {
    const day = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const date = today.getDate();
    const month = today.getMonth();
    const year = today.getFullYear();

    if (range === 'current_week') {
        // Monday to Sunday, week starts Monday
        // For Sunday (day=0), we want Monday of current week (tomorrow, so -(-1) = +1)
        // For Monday-Saturday, we want Monday of current week (days back)
        const daysBackToMonday = day === 0 ? -1 : day - 1;
        const monday = new Date(year, month, date - daysBackToMonday);
        const sunday = new Date(year, month, date - daysBackToMonday + 6);
        return {
            transaction_date_gte: toLocalDateKey(monday),
            transaction_date_lte: toLocalDateKey(sunday),
        };
    }

    if (range === 'previous_week') {
        // Previous Monday to Sunday
        const daysBackToMonday = day === 0 ? -1 : day - 1;
        const prevMonday = new Date(year, month, date - daysBackToMonday - 7);
        const prevSunday = new Date(year, month, date - daysBackToMonday - 1);
        return {
            transaction_date_gte: toLocalDateKey(prevMonday),
            transaction_date_lte: toLocalDateKey(prevSunday),
        };
    }

    if (range === 'current_month') {
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        return {
            transaction_date_gte: toLocalDateKey(firstDay),
            transaction_date_lte: toLocalDateKey(lastDay),
        };
    }

    if (range === 'previous_month') {
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);
        return {
            transaction_date_gte: toLocalDateKey(firstDay),
            transaction_date_lte: toLocalDateKey(lastDay),
        };
    }

    // Fallback (should not reach here)
    return {
        transaction_date_gte: toLocalDateKey(today),
        transaction_date_lte: toLocalDateKey(today),
    };
};

export const daysBetween = (a: string, b: string) =>
    Math.round(
        Math.abs(
            new Date(`${a}T00:00:00`).getTime() -
                new Date(`${b}T00:00:00`).getTime()
        ) / 86_400_000
    );

export const amountMatches = (
    receipt: Pick<Receipt, 'amount'>,
    tx: Pick<ExpenseTransaction, 'amount'>
) =>
    receipt.amount != null &&
    Math.abs(Number(receipt.amount) - Number(tx.amount)) < 0.015;

export const rankReceiptsForTransaction = (
    receipts: Receipt[],
    tx: ExpenseTransaction
): Receipt[] => {
    const sorted = [...receipts].sort((a, b) => {
        // 1. Amount match first
        const aMatches = amountMatches(a, tx);
        const bMatches = amountMatches(b, tx);
        if (aMatches !== bMatches) return aMatches ? -1 : 1;

        // 2. Then closest date (receipts with no date last)
        const aDate = a.transaction_date;
        const bDate = b.transaction_date;
        if (!aDate && !bDate) return b.id - a.id; // both null, id DESC
        if (!aDate) return 1; // a null, goes last
        if (!bDate) return -1; // b null, goes last
        const aDaysDiff = daysBetween(aDate, tx.transaction_date);
        const bDaysDiff = daysBetween(bDate, tx.transaction_date);
        if (aDaysDiff !== bDaysDiff) return aDaysDiff - bDaysDiff;

        // 3. Then id DESC
        return b.id - a.id;
    });
    return sorted;
};

export const receiptMatchIssues = (
    receipt: Pick<Receipt, 'amount' | 'transaction_date'>,
    tx: Pick<ExpenseTransaction, 'amount' | 'transaction_date'>
): string[] => {
    const issues: string[] = [];

    // Amount issue: only flag if receipt has an amount and they don't match
    if (receipt.amount != null && !amountMatches(receipt, tx)) {
        issues.push(`Amount differs: $${receipt.amount} vs $${tx.amount}`);
    }

    // Date issue: only flag if receipts have a date and differ by > 3 days
    if (receipt.transaction_date) {
        const days = daysBetween(receipt.transaction_date, tx.transaction_date);
        if (days > 3) {
            issues.push(`Date differs by ${days} days`);
        }
    }

    return issues;
};
